import React, { memo, useState, useEffect, useRef, useCallback } from 'react';

import Button from '@prisjakt/ui-button';

import * as S from './styles';

import { Grow, Variant } from './types';

interface Props {
  className?: string;
  children: React.ReactElement | Array<React.ReactElement | null>;
  grow?: Grow;
  toggleElement: React.ReactElement | string;
  showArrow?: boolean;
  variant: Variant;
}

const DropdownNew = (props: Props) => {
  const [expanded, setExpandedState] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    className,
    children,
    grow = 'none',
    toggleElement,
    showArrow,
    variant,
  } = props;

  const toggleDropdown = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setExpandedState(!expanded);
    },
    [expanded]
  );

  const handleClickOutside = (e: Event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setExpandedState(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <S.Dropdown
      variant={variant}
      expanded={expanded}
      ref={dropdownRef}
      className={className}
    >
      {typeof toggleElement === 'string' ? (
        <Button
          style={S.sharedButtonStyles}
          variant="text"
          color="link"
          onClick={toggleDropdown}
          iconPosition="right"
          icon={showArrow ? <S.Arrow expanded={expanded ? 1 : 0} /> : null}
        >
          {toggleElement}
        </Button>
      ) : (
        <toggleElement.type
          style={S.sharedButtonStyles}
          onClick={toggleDropdown}
          {...toggleElement.props}
        >
          {toggleElement.props.children}{' '}
          {showArrow && <S.Arrow expanded={expanded ? 1 : 0} />}
        </toggleElement.type>
      )}

      <S.DropdownContent
        variant={variant}
        expanded={expanded}
        grow={grow}
        nudge="UP"
        visible={expanded}
      >
        {React.Children.map(children, (child?: React.ReactElement | null) => {
          if (!child) {
            return null;
          }

          return child.props.onClick
            ? React.cloneElement(child, {
                ...child.props,
                onClick: (e: EventTarget) => {
                  if (child.props.onClick) {
                    child.props.onClick();
                  }

                  toggleDropdown(e);
                },
              })
            : child;
        })}
      </S.DropdownContent>
    </S.Dropdown>
  );
};

DropdownNew.defaultProps = {
  showArrow: true,
  variant: 'primary',
};

export default memo(DropdownNew);
